import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiArrowLeft } from "@mdi/js";
import { useDevice } from "app/contexts/DeviceContext";
import { fetchBookingConfig, setCurrentStep } from "./slice";
import { STEP_TITLES } from "./constants";
import BookingHeader from "./components/BookingHeader";
import BookingStepper from "./components/BookingStepper";
import StepContactAddress from "./components/StepContactAddress";
import StepPropertyDetails from "./components/StepPropertyDetails";
import StepSelectDateTime from "./components/StepSelectDateTime";
import StepConfirmBooking from "./components/StepConfirmBooking";
import BookingSuccess from "./components/BookingSuccess";
import BookingFailure from "./components/BookingFailure";
import BookingLinkError from "./components/BookingLinkError";
import BookingErrorPage from "./components/BookingErrorPage";
import LoadingSpinner from "app/components/LoadingSpinner";
import "./publicClientBooking.css";

const PublicClientBooking = () => {
  const { uniqueId } = useParams();
  const dispatch = useDispatch();
  const { isMobile, isTablet } = useDevice();

  const {
    companyInfo,
    companyLoading,
    companyError,
    currentStep,
    bookingOutcome,
  } = useSelector((state) => state.publicClientBooking);

  // Fetch booking config (company info) on mount using hashId
  useEffect(() => {
    if (uniqueId) {
      dispatch(fetchBookingConfig(uniqueId));
    }
  }, [dispatch, uniqueId]);

  // Update page title when company info is loaded
  useEffect(() => {
    if (companyInfo?.name) {
      document.title = `${companyInfo.name} | Inspection Booking`;
    } else {
      document.title = "Inspection Booking";
    }

    return () => {
      document.title = "Morph Inspect";
    };
  }, [companyInfo]);

  // Show loading state
  if (companyLoading) {
    return (
      <div className="public-client-booking-loading">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error state
  if (companyError) {
    return <BookingErrorPage isInitialLoadError />;
  }

  // Show nothing if company info not loaded yet
  if (!companyInfo) {
    return null;
  }

  // Outcome pages replace the full layout
  if (bookingOutcome === "success") return <BookingSuccess />;
  if (bookingOutcome === "failure") return <BookingFailure />;
  if (bookingOutcome === "link-error") return <BookingLinkError />;

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return <StepContactAddress />;
      case 1:
        return <StepPropertyDetails />;
      case 2:
        return <StepSelectDateTime />;
      case 3:
        return <StepConfirmBooking />;
      default:
        return <StepContactAddress />;
    }
  };

  return (
    <div className="public-client-booking-container">
      <BookingHeader
        companyName={companyInfo.name}
        companyLogo={companyInfo.logo}
      />

      <main className="public-client-booking-main">
        <div className="public-client-booking-back-row">
          {currentStep > 0 && (
            <button
              type="button"
              className="public-client-booking-back-btn"
              onClick={() => dispatch(setCurrentStep(currentStep - 1))}
            >
              <Icon path={mdiArrowLeft} size={0.625} />
              <span>Back</span>
            </button>
          )}
        </div>
        <div className="public-client-booking-content">
          {/* Left Column - Intro and Stepper */}
          <div className="public-client-booking-left-column">
            <div className="public-client-booking-intro">
              <h2 className="public-client-booking-title">
                Inspection Booking
              </h2>
              <p className="public-client-booking-subtitle">
                Schedule your property inspection appointment in a few simple
                steps.
              </p>
            </div>

            {/* Show current step title on tablet (mobile uses stepper's built-in label) */}
            {isTablet && (
              <h3 className="public-client-booking-step-title">
                {STEP_TITLES[currentStep]}
              </h3>
            )}

            {/* Stepper - vertical on desktop, horizontal on tablet/mobile */}
            <div className="public-client-booking-stepper-container">
              <BookingStepper
                currentStep={currentStep}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          </div>

          {/* Right Column - Form Content */}
          <div className="public-client-booking-right-column">
            {renderCurrentStep()}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicClientBooking;
